<script lang="ts">
	//UI
	import { DefaultLoading } from "@hitit/new_ui/src";
	import OrderTab from "./OrderTab.svelte";
	import PaymentTab from "./PaymentTab.svelte";
	import PointTab from "./PointTab.svelte";
	import RecordTab from "./RecordTab.svelte";

	//Image

	//Utils
	import type {
		OrderData,
		PaymentData,
		PaymentTransactionData,
	} from "@hitit/api";
	import type { AxiosResponse } from "axios";
	import { t, api } from "@hitit/hub/src/stores";

	export let orderResponse: OrderResponse;
	export let paymentResponse: PaymentResponse;
	export let selectOrderIndex: number;

	let editmode = false;
	let selectedTab = "order";
	let unwrapDTO: OrderData;
	let orderDetailPath: string;
	let dumyOrderStatus: string | undefined = undefined;

	const setBodyOverflow = (style: "unset" | "hidden") =>
		(document.body.style.overflow = style);

	type OrderResponse = Promise<AxiosResponse<{ orderDTO: OrderData }>>;
	type PaymentResponse = Promise<AxiosResponse<{ paymentDTOs: PaymentData[] }>>;

	let showTransactionData: PaymentTransactionData | null = null;

	function tabHandler(tab: string) {
		selectedTab = tab;
	}

	function paymentCancelHandler(detail: { orderStatus: string }) {
		const { orderStatus } = detail;
		dumyOrderStatus = orderStatus;
	}

	async function orderDTOPUnwrap() {
		const orderDTO = (await orderResponse).data.orderDTO;
		const r = await $api.getOrder(orderDTO.storeid!, orderDTO.id!);
		unwrapDTO = r.data.orderDTO;
		orderDetailPath = `${window.location.host}/store/orderDetail/${unwrapDTO.storeid}/${unwrapDTO.id}`;
	}

	function earnedOrderUpdate(earnedOrderDTO: OrderData) {
		unwrapDTO = earnedOrderDTO;
	}

	function earnedCancelOrderUpdate(earnedOrderDTO: OrderData) {
		unwrapDTO = earnedOrderDTO;
	}

	$: orderDTOPUnwrap();
	$: showTransactionData ? setBodyOverflow("hidden") : setBodyOverflow("unset");
</script>

<div class="container" t-id="OrderDetail">
	{#if unwrapDTO}
		<div class="tab-wrapper">
			<div class="top-tab">
				<div
					class="order tab"
					class:selected={selectedTab === "order"}
					on:click={() => tabHandler("order")}
				>
					{$t("hub.order.order_info")}
				</div>
				<div
					class="payment tab"
					class:selected={selectedTab === "payment"}
					on:click={() => tabHandler("payment")}
				>
					{$t("hub.order.payment_info")}
				</div>
			</div>
			<div class="bottom-tab">
				<div
					class="point tab"
					class:selected={selectedTab === "point"}
					on:click={() => tabHandler("point")}
				>
					{$t("hub.order.point_info")}
				</div>
				<div
					class="record tab"
					class:selected={selectedTab === "record"}
					on:click={() => tabHandler("record")}
				>
					{$t("hub.order.record")}
				</div>
			</div>
		</div>
		<div class="detail-inner-container">
			{#if selectedTab === "order"}
				<OrderTab
					on:CHANGE_IMAGE
					selectOrderIndex={selectOrderIndex}
					storeId={unwrapDTO.storeid || ""}
					orderId={unwrapDTO.id || ""}
					paymentResponse={paymentResponse}
					orderDetailPath={orderDetailPath}
					orderStatus={dumyOrderStatus}
				/>
			{:else if selectedTab === "payment"}
				<PaymentTab
					on:PAYMENT_CANCEL={(e) => paymentCancelHandler(e.detail)}
					orderUnwrapDTO={unwrapDTO}
					orderId={unwrapDTO.id}
				/>
			{:else if selectedTab === "point"}
				<PointTab
					orderUnwrapDTO={unwrapDTO}
					on:EARNED_UPDATE_ORDER={(e) => earnedOrderUpdate(e.detail)}
					on:EARNED_CANCEL_UPDATE_ORDER={(e) =>
						earnedCancelOrderUpdate(e.detail)}
				/>
			{:else if selectedTab === "record"}
				<RecordTab />
			{/if}
		</div>
	{:else}
		<div class="loading">
			<DefaultLoading />
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../../variables.scss";
	/* mobile First */
	div {
		box-sizing: border-box;
	}

	.container {
		display: flex;
		flex-direction: column;
		background-color: #e6e7ed;

		.tab-wrapper {
			display: flex;
			flex-direction: column;
			width: 100%;
			justify-content: center;
			align-items: center;
			box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.08);
			z-index: 1;

			.tab {
				width: 100%;
				height: 39px;
				min-width: 120px;
				display: flex;
				align-items: center;
				justify-content: center;
				font-weight: 400;
				cursor: pointer;
				@include appleAdjustTextForSize(16);

				&.selected {
					border-bottom: 2px solid #3c68d6;
					color: #3c68d6;
					font-weight: 550;
				}
			}
			.top-tab {
				width: 100%;
				display: flex;
				justify-content: center;
			}
			.bottom-tab {
				width: 100%;
				display: flex;
				justify-content: center;
			}
		}

		.detail-inner-container {
			display: flex;
			flex-direction: column;
			gap: 10px;
			width: 100%;
			background-color: #e6e7ed;
			padding: 20px 10px;
		}

		.loading {
			width: 30px;
			height: 30px;
			display: flex;
			justify-content: center;
			align-items: center;
			margin: 0 auto;
		}
	}

	@include DesktopSize {
		.container {
			flex-direction: row;
			font-size: 1.25rem;
			flex-wrap: wrap;

			.tab-wrapper {
				flex-direction: row;
				width: 100%;
				height: 54px;
				.tab {
					@include appleAdjustTextForSize(16, false);
					height: 54px;
				}
			}

			.detail-inner-container {
				display: flex;
				flex-direction: column;
				gap: 20px;
				width: 100%;
				background-color: #e6e7ed;
				padding: 30px;
			}
		}
	}
</style>